/**
 * Stock endpoints: the paginated store stocks, the central stocks with the
 * reserved ones, and the status of the last import job.
 */
import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';
import { z } from 'zod';
import { api } from '@/shared/api/client';

const stockSchema = z.object({
  _id: z.string().nullish(),
  storeName: z.string().nullish(),
  storeCode: z.union([z.string(), z.number()]).nullish(),
  ean: z.string().nullish(),
  sku: z.string().nullish(),
  quantity: z.number().nullish(),
  lastImportId: z.string().nullish(),
});

export type Stock = z.infer<typeof stockSchema>;

const stockPageSchema = z.object({
  data: z.array(stockSchema),
  nextCursor: z.string().nullish(),
});

export type StockPage = z.infer<typeof stockPageSchema>;

const reservedStockSchema = z.object({
  ean13CentralFlow: z.string().nullish(),
  m3SkuCentralFlow: z.string().nullish(),
});

export type ReservedStock = z.infer<typeof reservedStockSchema>;

const lastApiCallSchema = z
  .object({ lastUpdate: z.string().nullish() })

  .nullable();

export type LastApiCall = z.infer<typeof lastApiCallSchema>;

export type StockType = 'stock' | 'centralStock';

export interface StockListParams {
  search: string;
  sortBy: string | null;
  sortOrder: 'asc' | 'desc';
  /** Bumped by the refresh button: reloads from the first page. */
  refresh: number;
}

/** GET stock?type=stock, one page after another (`after` cursor). */
export const stockPagesQueryOptions = ({
  search,
  sortBy,
  sortOrder,
  refresh,
}: StockListParams) =>
  infiniteQueryOptions({
    queryKey: [
      'stocks',
      'stock',
      { search, sortBy, sortOrder, refresh },
    ] as const,
    queryFn: ({ pageParam }) =>
      api.get<StockPage>('stock', {
        query: {
          type: 'stock',
          sortOrder,
          ...(sortBy ? { sortBy } : {}),
          ...(search ? { search } : {}),
          ...(pageParam ? { after: pageParam } : {}),
        },
        schema: stockPageSchema,
      }),
    initialPageParam: '' as string,
    getNextPageParam: (page) => page.nextCursor ?? undefined,
    retry: false,
  });

/** GET stock?type=centralStock and GET stock/reserved, loaded together. */
export const centralStocksQueryOptions = (refresh: number) =>
  queryOptions({
    queryKey: ['stocks', 'centralStock', refresh] as const,
    queryFn: async () => {
      const [stocks, reserved] = await Promise.all([
        api.get<Stock[]>('stock', {
          query: { type: 'centralStock' },
          schema: z.array(stockSchema),
        }),
        api.get<ReservedStock[]>('stock/reserved', {
          schema: z.array(reservedStockSchema),
        }),
      ]);
      return { stocks, reserved };
    },
    retry: false,
  });

/** GET stock/last-api-call?type=: the last import job of the given stock type. */
export const lastApiCallQueryOptions = (type: StockType, refresh: number) =>
  queryOptions({
    queryKey: ['stocks', 'last-api-call', type, refresh] as const,
    queryFn: () =>
      api.get<LastApiCall>('stock/last-api-call', {
        query: { type },
        schema: lastApiCallSchema,
      }),
    retry: false,
  });
